<template>
  <component :is="linkTag" v-bind="linkAttributes" class="component-link__container" data-test-component-link>
    <slot></slot>
  </component>
</template>

<script>
import { defineComponent, toRefs, computed } from 'vue'

export default defineComponent({
  name: 'IdfComponentLink',
  props: {
    url: {
      type: String,
      default: '',
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { url, isMobile } = toRefs(props)

    const compareUrl = (linkA, linkB, compareFn) => {
      if (!linkA || !linkB) return false
      try {
        const linkAUrlObject = new URL(linkA)
        const linkBUrlObject = new URL(linkB)
        return compareFn(linkAUrlObject, linkBUrlObject)
      } catch (_) {
        return false
      }
    }

    const checkDifferentOrigin = (urlObjectA, urlObjectB) => {
      return urlObjectA.origin !== urlObjectB.origin
    }

    const isHash = (link) => {
      if (!link) return false
      return link.startsWith('#')
    }

    const isExternalLink = (link, currentLocation) => {
      if (isHash(link)) return false
      return compareUrl(link, currentLocation, checkDifferentOrigin)
    }

    const isExternal = computed(() => isExternalLink(url.value, window.location.href))

    const getQueryParameters = (link) => {
      // link is like 'https://www.aladin.farm/recherche?hierarchicalMenu[categoriesLevel0][0]=Santé du végétal&hierarchicalMenu[categoriesLevel0][1]=Molluscicides'
      // or https://www.aladin.farm/recherche?hierarchicalMenu[categoriesLevel0][0]=Santé du végétal&refinementList[crops][1]=Vigne&refinementList[crops][2]=Vigne de table&refinementList[crops][3]=Vigne-mère
      const emptyQuery = ''
      const queryStrings = link && link.split('?')[1] ? link.split('?')[1] : emptyQuery

      const queryObject = {}
      const urlParameters = queryStrings.split('&')
      if (urlParameters) {
        urlParameters.forEach((parameter) => {
          const [key, value] = parameter.split('=')
          if (key) {
            queryObject[decodeURIComponent(key)] = value !== undefined ? decodeURIComponent(value) : true
          }
        })
      }
      return queryObject
    }

    const urlToRoute = (link) => {
      const homeRoute = { path: '/' }
      if (link) {
        if (link.name || link.path) {
          return link
        }
        try {
          const urlObject = new URL(link)
          return { path: urlObject.pathname, query: getQueryParameters(link) }
        } catch (_) {
          return homeRoute
        }
      }
      return homeRoute
    }

    const linkAttributes = computed(() => {
      if (isExternal.value) {
        const attributes = {
          href: url.value,
        }
        if (!isMobile.value) {
          attributes.target = '_blank'
          attributes.rel = 'noopener noreferrer'
        }
        return attributes
      }
      return {
        to: urlToRoute(url.value),
      }
    })

    const linkTag = computed(() => {
      if (!url.value) return 'span'
      return isExternal.value ? 'a' : 'router-link'
    })

    return {
      linkAttributes,
      linkTag,
    }
  },
})
</script>
